import api from '@/services/api';
import type {
  Campaign,
  Package,
  PackageItem,
  PatientJourney,
  CreateCampaignRequest,
  UpdateCampaignRequest,
  CreatePackageRequest,
  UpdatePackageRequest,
  AddPackageItemRequest,
  ReorderPackageItemsRequest,
  EnrollPatientRequest,
  CampaignQueryParams,
} from '@/types/campaign';

const BASE_PATH = '/campaigns';

/**
 * Campaign management API service.
 *
 * Handles CRUD operations for campaigns, packages, package items,
 * patient enrollment, and journey tracking.
 */
export const campaignService = {
  // ── Campaign CRUD ──────────────────────────────────────

  /**
   * Lists all campaigns, optionally filtered by status or search term.
   */
  async getCampaigns(params?: CampaignQueryParams): Promise<Campaign[]> {
    const { data } = await api.get<{ items: Campaign[] }>(BASE_PATH, { params });
    return data.items;
  },

  /**
   * Fetches a single campaign by ID, including packages.
   */
  async getCampaign(id: string): Promise<Campaign> {
    const { data } = await api.get<Campaign>(`${BASE_PATH}/${id}`);
    return data;
  },

  /**
   * Creates a new campaign.
   */
  async createCampaign(payload: CreateCampaignRequest): Promise<Campaign> {
    const { data } = await api.post<Campaign>(BASE_PATH, payload);
    return data;
  },

  /**
   * Updates an existing campaign.
   */
  async updateCampaign(id: string, payload: UpdateCampaignRequest): Promise<Campaign> {
    const { data } = await api.put<Campaign>(`${BASE_PATH}/${id}`, payload);
    return data;
  },

  /**
   * Activates a draft campaign.
   */
  async activateCampaign(id: string): Promise<Campaign> {
    const { data } = await api.put<Campaign>(`${BASE_PATH}/${id}/activate`);
    return data;
  },

  /**
   * Pauses an active campaign.
   */
  async pauseCampaign(id: string): Promise<Campaign> {
    const { data } = await api.put<Campaign>(`${BASE_PATH}/${id}/pause`);
    return data;
  },

  /**
   * Resumes a paused campaign.
   */
  async resumeCampaign(id: string): Promise<Campaign> {
    const { data } = await api.put<Campaign>(`${BASE_PATH}/${id}/resume`);
    return data;
  },

  /**
   * Finishes a campaign (active or paused).
   */
  async finishCampaign(id: string): Promise<Campaign> {
    const { data } = await api.put<Campaign>(`${BASE_PATH}/${id}/finish`);
    return data;
  },

  // ── Package CRUD ───────────────────────────────────────

  /**
   * Lists packages for a campaign.
   */
  async getPackages(campaignId: string): Promise<Package[]> {
    const { data } = await api.get<Package[]>(
      `${BASE_PATH}/${campaignId}/packages`,
    );
    return data;
  },

  /**
   * Creates a new package within a campaign.
   */
  async createPackage(
    campaignId: string,
    payload: CreatePackageRequest,
  ): Promise<Package> {
    const { data } = await api.post<Package>(
      `${BASE_PATH}/${campaignId}/packages`,
      payload,
    );
    return data;
  },

  /**
   * Updates an existing package.
   */
  async updatePackage(
    campaignId: string,
    packageId: string,
    payload: UpdatePackageRequest,
  ): Promise<Package> {
    const { data } = await api.put<Package>(
      `${BASE_PATH}/${campaignId}/packages/${packageId}`,
      payload,
    );
    return data;
  },

  /**
   * Deletes a package from a campaign.
   */
  async deletePackage(campaignId: string, packageId: string): Promise<void> {
    await api.delete(`${BASE_PATH}/${campaignId}/packages/${packageId}`);
  },

  // ── Package Items ──────────────────────────────────────

  /**
   * Adds an item (procedure type) to a package.
   */
  async addPackageItem(
    campaignId: string,
    packageId: string,
    payload: AddPackageItemRequest,
  ): Promise<PackageItem> {
    const { data } = await api.post<PackageItem>(
      `${BASE_PATH}/${campaignId}/packages/${packageId}/items`,
      payload,
    );
    return data;
  },

  /**
   * Removes an item from a package.
   */
  async removePackageItem(
    campaignId: string,
    packageId: string,
    itemId: string,
  ): Promise<void> {
    await api.delete(
      `${BASE_PATH}/${campaignId}/packages/${packageId}/items/${itemId}`,
    );
  },

  /**
   * Reorders items within a package.
   */
  async reorderPackageItems(
    campaignId: string,
    packageId: string,
    payload: ReorderPackageItemsRequest,
  ): Promise<PackageItem[]> {
    const { data } = await api.put<PackageItem[]>(
      `${BASE_PATH}/${campaignId}/packages/${packageId}/items/reorder`,
      payload,
    );
    return data;
  },

  // ── Patient Enrollment & Journeys ─────────────────────

  /**
   * Enrolls a patient in a campaign package.
   */
  async enrollPatient(
    campaignId: string,
    payload: EnrollPatientRequest,
  ): Promise<PatientJourney> {
    const { data } = await api.post<PatientJourney>(
      `${BASE_PATH}/${campaignId}/enroll`,
      payload,
    );
    return data;
  },

  /**
   * Lists patient journeys for a campaign.
   */
  async getJourneys(
    campaignId: string,
    options?: { signal?: AbortSignal },
  ): Promise<PatientJourney[]> {
    const { data } = await api.get<{ items: PatientJourney[] }>(
      `${BASE_PATH}/${campaignId}/journeys`,
      { signal: options?.signal },
    );
    return data.items;
  },

  /**
   * Fetches a single patient journey by ID.
   */
  async getJourney(
    campaignId: string,
    journeyId: string,
  ): Promise<PatientJourney> {
    const { data } = await api.get<PatientJourney>(
      `${BASE_PATH}/${campaignId}/journeys/${journeyId}`,
    );
    return data;
  },
};
